import { Confirm } from "@pej/kore/components";
import { useBlocker } from "@tanstack/react-router";
import { type ReactNode, useCallback } from "react";

/**
 * Unified "unsaved changes" guard for page-level forms, ported from the
 * vouchers admin (`lib/forms/use-unsaved-changes-guard.tsx`). Every exit path
 * that could lose edits — an explicit Cancel button, in-app router
 * navigation (sidebar, header, back/forward), and external exits (tab close,
 * refresh, URL typed in the bar, via `enableBeforeUnload`) — is funnelled
 * through TanStack Router's `useBlocker`, so there's exactly one trigger and
 * one state machine instead of a parallel dialog per exit path. The hook
 * just renders the Confirm bound to that state.
 *
 * Consumers render the returned `dialog` node once. Keep this hook
 * page-level — sheets/drawers manage their own close semantics and a second
 * blocker would clash with the host page's.
 */
export function useUnsavedChangesGuard(isDirty: () => boolean): {
    dialog: ReactNode;
} {
    // `isDirty` is a getter, not a captured boolean, so `shouldBlockFn` and
    // `enableBeforeUnload` read live state on each invocation. This is load-
    // bearing for the "submit-success → navigate" flow: the caller calls
    // `form.reset(values)` synchronously before `navigate(...)`, RHF updates
    // its internal state synchronously, the subsequent shouldBlockFn call
    // reads the fresh value, blocker doesn't fire. A captured boolean would
    // be stale (closure from last render) and would spuriously block.
    const blocker = useBlocker({
        shouldBlockFn: () => isDirty(),
        withResolver: true,
        // Wire native beforeunload only while dirty so users never see the
        // generic "leave site?" prompt when they have nothing to lose.
        enableBeforeUnload: () => isDirty(),
    });

    const blocked = blocker.status === "blocked";

    const handleDialogOpenChange = useCallback(
        (open: boolean) => {
            if (open) return;
            // Dismissed via Cancel button / Esc / backdrop — keep editing,
            // unblock the in-flight navigation.
            if (blocker.status === "blocked") blocker.reset();
        },
        [blocker],
    );

    const handleConfirm = useCallback(() => {
        if (blocker.status === "blocked") blocker.proceed();
    }, [blocker]);

    const dialog = (
        <Confirm
            open={blocked}
            onOpenChange={handleDialogOpenChange}
            title="Discard unsaved changes?"
            description="Your edits to this form will be lost."
            primaryLabel="Discard"
            cancelLabel="Keep editing"
            variant="destructive"
            onConfirm={handleConfirm}
        />
    );

    return { dialog };
}
